import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function formatDateTime(value) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStanding(user) {
  if (user?.isBanned) {
    return {
      label: 'Banned',
      background: '#fee2e2',
      color: '#991b1b',
    };
  }

  if (user?.isTempBanned) {
    return {
      label: 'Temporarily banned',
      background: '#ffedd5',
      color: '#9a3412',
    };
  }

  if (user?.membershipCardRequired || user?.membershipStatus === 'card_required') {
    return {
      label: 'Card required',
      background: '#fef3c7',
      color: '#92400e',
    };
  }

  if (user?.membershipStatus === 'card_issue') {
    return {
      label: 'Card issue',
      background: '#fee2e2',
      color: '#991b1b',
    };
  }

  if (Number(user?.membershipNoShowStrikes || 0) === 1 || user?.hasWarning) {
    return {
      label: 'Warning',
      background: '#fef3c7',
      color: '#92400e',
    };
  }

  if (user?.membershipActive === false) {
    return {
      label: 'Inactive',
      background: '#e5e7eb',
      color: '#374151',
    };
  }

  return {
    label: 'Good standing',
    background: '#dcfce7',
    color: '#166534',
  };
}

function getUserName(user) {
  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  return name || 'Unnamed user';
}

function getReservations(user) {
  if (Array.isArray(user?.reservations)) return user.reservations;
  if (Array.isArray(user?.tickets)) return user.tickets;
  return [];
}

function getReservationEvent(reservation) {
  return reservation?.event || reservation?.eventId || null;
}

function getReservationStatus(reservation) {
  return (
    reservation?.attendanceStatus ||
    reservation?.status ||
    'reserved'
  );
}

function statusLabel(value) {
  const normalized = String(value || '').toLowerCase();

  const labels = {
    reserved: 'Reserved',
    active: 'Reserved',
    checked_in: 'Checked in',
    attended: 'Attended',
    canceled: 'Canceled',
    cancelled: 'Canceled',
    late_cancel: 'Late cancel',
    no_show: 'No-show',
    refunded: 'Refunded',
  };

  return labels[normalized] || value || 'Reserved';
}

function reservationStatusStyle(value) {
  const normalized = String(value || '').toLowerCase();

  if (['checked_in', 'attended'].includes(normalized)) {
    return { background: '#dcfce7', color: '#166534' };
  }

  if (['no_show', 'late_cancel'].includes(normalized)) {
    return { background: '#fee2e2', color: '#991b1b' };
  }

  if (['canceled', 'cancelled', 'refunded'].includes(normalized)) {
    return { background: '#e5e7eb', color: '#374151' };
  }

  return { background: '#fef3c7', color: '#92400e' };
}

function isWithinDays(dateValue, days) {
  if (!dateValue) return false;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return date.getTime() >= cutoff;
}

export default function AdminUsers() {
const [users, setUsers] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState('');
const [search, setSearch] = useState('');
const [dateFilter, setDateFilter] = useState('7');
const [reservationFilter, setReservationFilter] = useState('all');
const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('adminToken') || '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.users)
          ? response.data.users
          : [];

      setUsers(rows);
    } catch (err) {
      console.error('[AdminUsers fetchUsers]', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load users.'
      );
    } finally {
      setLoading(false);
    }
  }

  const filteredUsers = useMemo(() => {
  const query = search.trim().toLowerCase();

  return [...users]
    .filter((user) => {
      if (dateFilter === 'all') return true;

      return isWithinDays(
        user?.createdAt,
        Number(dateFilter)
      );
    })
    .filter((user) => {
      const reservations =
        getReservations(user);

      const hasReservations =
        reservations.length > 0;

      if (
        reservationFilter === 'with' &&
        !hasReservations
      ) {
        return false;
      }

      if (
        reservationFilter === 'without' &&
        hasReservations
      ) {
        return false;
      }

      return true;
    })
    .filter((user) => {
      if (!query) return true;

      const haystack = [
        user?.firstName,
        user?.lastName,
        user?.email,
        user?.phoneNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    })
    .sort((a, b) => {
      const aTime = new Date(
        a?.createdAt || 0
      ).getTime();

      const bTime = new Date(
        b?.createdAt || 0
      ).getTime();

      return bTime - aTime;
    });
}, [
  users,
  search,
  dateFilter,
  reservationFilter,
]);
  const summary = useMemo(() => {
    const total = users.length;
    const newSevenDays = users.filter((user) =>
      isWithinDays(user?.createdAt, 7)
    ).length;
    const withReservations = users.filter(
      (user) => getReservations(user).length > 0
    ).length;
    const goodStanding = users.filter(
      (user) => getStanding(user).label === 'Good standing'
    ).length;

    return {
      total,
      newSevenDays,
      withReservations,
      goodStanding,
    };
  }, [users]);

  if (loading) {
    return <div style={{ padding: 20 }}>Loading users…</div>;
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Users</h2>
          <div style={{ color: '#6b7280', marginTop: 4 }}>
            Newest accounts appear first.
          </div>
        </div>

        <button type="button" onClick={fetchUsers}>
          Refresh
        </button>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            background: '#fee2e2',
            color: '#991b1b',
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
      <div
  style={{
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#fff',
  }}
>
  <div
    style={{
      color: '#6b7280',
      fontSize: 12,
    }}
  >
    Total users
  </div>

  <div
    style={{
      fontWeight: 900,
      fontSize: 24,
      marginTop: 4,
    }}
  >
    {summary.total}
  </div>
</div>

<div
  style={{
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#fff',
  }}
>
  <div
    style={{
      color: '#6b7280',
      fontSize: 12,
    }}
  >
    New users · 7d
  </div>

  <div
    style={{
      fontWeight: 900,
      fontSize: 24,
      marginTop: 4,
    }}
  >
    {summary.newSevenDays}
  </div>
</div>

<div
  style={{
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#fff',
  }}
>
  <div
    style={{
      color: '#6b7280',
      fontSize: 12,
    }}
  >
    Good standing
  </div>

  <div
    style={{
      fontWeight: 900,
      fontSize: 24,
      marginTop: 4,
    }}
  >
    {summary.goodStanding}
  </div>
</div>

<button
  type="button"
  onClick={() =>
    setReservationFilter(
      reservationFilter === 'with'
        ? 'all'
        : 'with'
    )
  }
  style={{
    border:
      reservationFilter === 'with'
        ? '2px solid #111'
        : '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#fff',
    textAlign: 'left',
    cursor: 'pointer',
  }}
>
  <div
    style={{
      color: '#6b7280',
      fontSize: 12,
    }}
  >
    With upcoming reservations
  </div>

  <div
    style={{
      fontWeight: 900,
      fontSize: 24,
      marginTop: 4,
    }}
  >
    {summary.withReservations}
  </div>
</button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, email, or phone"
          style={{
            width: '100%',
            maxWidth: 360,
            height: 40,
            border: '1px solid #d1d5db',
            borderRadius: 10,
            padding: '0 12px',
          }}
        />

    <div
  style={{
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  }}
>
  <select
    value={dateFilter}
    onChange={(event) =>
      setDateFilter(event.target.value)
    }
    style={{
      height: 40,
      border: '1px solid #d1d5db',
      borderRadius: 10,
      padding: '0 10px',
      background: '#fff',
    }}
  >
    <option value="1">
      Last 24 hours
    </option>

    <option value="7">
      Last 7 days
    </option>

    <option value="30">
      Last 30 days
    </option>

    <option value="all">
      All users
    </option>
  </select>

  <select
    value={reservationFilter}
    onChange={(event) =>
      setReservationFilter(
        event.target.value
      )
    }
    style={{
      height: 40,
      border: '1px solid #d1d5db',
      borderRadius: 10,
      padding: '0 10px',
      background: '#fff',
    }}
  >
    <option value="all">
      All reservation statuses
    </option>

    <option value="with">
      With upcoming reservations
    </option>

    <option value="without">
      Without upcoming reservations
    </option>
  </select>
</div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            minWidth: 1050,
            borderCollapse: 'collapse',
            background: '#fff',
          }}
        >
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Standing</th>
              <th style={styles.th}>Account created</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Reserved events</th>
              <th style={styles.th}>Details</th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((user) => {
              const standing = getStanding(user);
              const reservations = getReservations(user);

              return (
                <tr key={user._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          background: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                          flexShrink: 0,
                        }}
                      >
                        {user?.profilePicUrl ? (
                          <img
                            src={user.profilePicUrl}
                            alt={getUserName(user)}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : null}
                      </div>

                      <div>
                        <div style={{ fontWeight: 900 }}>
                          {getUserName(user)}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                          {user?.city || '—'}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td style={styles.td}>
                    <span
                      style={{
                        display: 'inline-flex',
                        borderRadius: 999,
                        padding: '5px 9px',
                        fontSize: 12,
                        fontWeight: 800,
                        background: standing.background,
                        color: standing.color,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {standing.label}
                    </span>
                  </td>

                  <td style={styles.td}>{formatDateTime(user?.createdAt)}</td>
                  <td style={styles.td}>{user?.email || '—'}</td>
                  <td style={styles.td}>{user?.phoneNumber || '—'}</td>

                  <td style={styles.td}>
                    <div style={{ fontWeight: 800 }}>
                      {reservations.length}{' '}
                      {reservations.length === 1 ? 'event' : 'events'}
                    </div>

                    {reservations.slice(0, 2).map((reservation) => {
                      const event = getReservationEvent(reservation);
                      return (
                        <div
                          key={reservation?._id || `${event?._id}-${event?.name}`}
                          style={{
                            marginTop: 5,
                            color: '#6b7280',
                            fontSize: 12,
                            maxWidth: 260,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {event?.name || 'Unknown event'}
                        </div>
                      );
                    })}

                    {reservations.length > 2 ? (
                      <div style={{ marginTop: 5, color: '#6b7280', fontSize: 12 }}>
                        +{reservations.length - 2} more
                      </div>
                    ) : null}
                  </td>

                  <td style={styles.td}>
                    <button
                      type="button"
                      onClick={() => setSelectedUser(user)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!filteredUsers.length ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No users found.
        </div>
      ) : null}

      {selectedUser ? (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      ) : null}
    </div>
  );
}

function UserDetailsModal({ user, onClose }) {
  const reservations = getReservations(user);
  const standing = getStanding(user);

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          height: '100%',
          overflowY: 'auto',
          background: '#fff',
          padding: 22,
          boxShadow: '-12px 0 40px rgba(0,0,0,0.15)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>User details</h3>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 22,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: '50%',
              overflow: 'hidden',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              flexShrink: 0,
            }}
          >
            {user?.profilePicUrl ? (
              <img
                src={user.profilePicUrl}
                alt={getUserName(user)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : null}
          </div>

          <div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              {getUserName(user)}
            </div>
            <div style={{ color: '#6b7280', marginTop: 4 }}>
              {user?.email || '—'}
            </div>
            <div style={{ color: '#6b7280', marginTop: 2 }}>
              {user?.phoneNumber || '—'}
            </div>
          </div>
        </div>

        <div style={styles.detailCard}>
          <div style={styles.detailTitle}>Standing</div>
          <span
            style={{
              display: 'inline-flex',
              borderRadius: 999,
              padding: '5px 9px',
              fontSize: 12,
              fontWeight: 800,
              background: standing.background,
              color: standing.color,
            }}
          >
            {standing.label}
          </span>

          <DetailRow label="Membership status" value={user?.membershipStatus || '—'} />
          <DetailRow
            label="No-show strikes"
            value={String(user?.membershipNoShowStrikes || 0)}
          />
          <DetailRow
            label="Attendance streak"
            value={String(user?.reservationAttendanceStreak || 0)}
          />
          <DetailRow
            label="Reservations used"
            value={`${user?.membershipReservationsUsed || 0} / ${user?.membershipReservationLimit || 0}`}
          />
        </div>

        <div style={styles.detailCard}>
          <div style={styles.detailTitle}>Account</div>
          <DetailRow label="Created" value={formatDateTime(user?.createdAt)} />
          <DetailRow label="City" value={user?.city || '—'} />
          <DetailRow label="Gender" value={user?.gender || '—'} />
          <DetailRow label="User ID" value={String(user?._id || '—')} />
        </div>

        <div style={styles.detailCard}>
          <div style={styles.detailTitle}>
            Reserved events ({reservations.length})
          </div>

          {!reservations.length ? (
            <div style={{ color: '#6b7280' }}>No reservations yet.</div>
          ) : (
            reservations.map((reservation) => {
              const event = getReservationEvent(reservation);
              const status = getReservationStatus(reservation);
              const statusStyle = reservationStatusStyle(status);

              return (
                <div
                  key={reservation?._id || `${event?._id}-${event?.name}`}
                  style={{
                    borderTop: '1px solid #e5e7eb',
                    padding: '12px 0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 900 }}>
                        {event?.name || 'Unknown event'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
                        {[event?.eventDate, event?.startTime12]
                          .filter(Boolean)
                          .join(' · ') || 'Date unavailable'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                        Reserved {formatDateTime(reservation?.purchasedAt || reservation?.createdAt)}
                      </div>
                    </div>

                    <span
                      style={{
                        borderRadius: 999,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        ...statusStyle,
                      }}
                    >
                      {statusLabel(status)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 10,
      }}
    >
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </span>
    </div>
  );
}

const styles = {
  th: {
    padding: 12,
    borderBottom: '1px solid #d1d5db',
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: 12,
    verticalAlign: 'top',
    fontSize: 13,
  },
  detailCard: {
    marginTop: 18,
    padding: 16,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
  },
  detailTitle: {
    fontWeight: 900,
    fontSize: 15,
    marginBottom: 12,
  },
};